import React, { useState, useEffect } from 'react';
import { IMAGE_URLS } from './constants';
import { PolaroidPosition } from './types';
import { Polaroid } from './components/Polaroid';
import { StartScreen } from './components/StartScreen';
import { JellyBeanBackground } from './components/JellyBeanBackground';
import { Heart } from 'lucide-react';

const App: React.FC = () => {
  const [started, setStarted] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [polaroids, setPolaroids] = useState<PolaroidPosition[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [lastClickedId, setLastClickedId] = useState<number | null>(null);
  const [maxZIndex, setMaxZIndex] = useState(100);

  useEffect(() => {
    // Generate random positions on client-side mount
    const positions: PolaroidPosition[] = IMAGE_URLS.map((img, index) => {
      let top, left;
      const angle = (index / IMAGE_URLS.length) * 2 * Math.PI; 
      const radiusX = 35 + Math.random() * 10; 
      const radiusY = 35 + Math.random() * 10; 
      
      left = 50 + radiusX * Math.cos(angle);
      top = 50 + radiusY * Math.sin(angle);

      left = Math.max(10, Math.min(90, left));
      top = Math.max(10, Math.min(90, top));

      const startLeft = left < 50 ? -20 : 120;
      const startTop = top < 50 ? -20 : 120;

      return {
        id: img.id,
        url: img.url,
        caption: img.caption,
        orientation: img.orientation,
        top,
        left,
        rotation: Math.random() * 30 - 15,
        delay: index * 0.2 + 0.5,
        startTop,
        startLeft,
        zIndex: Math.floor(Math.random() * 10) + 10,
      };
    });

    setPolaroids(positions);
  }, []);

  useEffect(() => {
    if (started) {
      const timer = setTimeout(() => {
        setMounted(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [started]);

  const handlePolaroidClick = (id: number) => {
    if (activeId === id) return;

    if (lastClickedId === id) {
      setActiveId(id);
    } else {
      setLastClickedId(id);
      const newMaxZ = maxZIndex + 1;
      setMaxZIndex(newMaxZ);
      
      setPolaroids(prev => prev.map(p => 
        p.id === id ? { ...p, zIndex: newMaxZ } : p
      ));
    }
  };

  const handleBackgroundClick = () => {
    if (activeId !== null) {
      setActiveId(null);
    }
  };

  return (
    <div 
      className="relative w-screen h-screen bg-gradient-to-br from-pink-100 via-red-50 to-pink-200 overflow-hidden flex items-center justify-center"
      onClick={handleBackgroundClick}
    >
      {/* Space-like Jelly Bean Background */}
      <JellyBeanBackground />
      
      {/* Background Decor Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        <div className="absolute top-10 left-10 text-pink-200 opacity-40 transform rotate-12">
            <Heart size={120} fill="currentColor" />
        </div>
        <div className="absolute bottom-10 right-10 text-pink-200 opacity-40 transform -rotate-12">
            <Heart size={150} fill="currentColor" />
        </div>
      </div>

      {!started ? (
        <StartScreen onStart={() => setStarted(true)} />
      ) : (
        <>
          {/* Polaroids Layer */}
          <div className="absolute inset-0 w-full h-full z-20">
            {polaroids.map((polaroid) => (
              <Polaroid 
                key={polaroid.id} 
                data={polaroid} 
                isMounted={mounted} 
                isActive={activeId === polaroid.id}
                onClick={() => handlePolaroidClick(polaroid.id)}
              />
            ))}
          </div>

          {/* Central Message Layer */}
          <div className={`relative z-50 text-center pointer-events-none mix-blend-multiply transition-opacity duration-500 ${activeId ? 'opacity-10 blur-sm' : 'opacity-60'}`}>
            <div className="animate-heartbeat flex flex-col items-center justify-center">
                <div className="relative uppercase">
                    <h1 className="font-strong text-6xl md:text-8xl lg:text-9xl text-red-600 drop-shadow-lg select-none"
                        style={{ textShadow: '2px 2px 0px rgba(255,255,255,0.8), 4px 4px 10px rgba(0,0,0,0.1)' }}
                    >
                    Happy
                  </h1>
                  <h1 className="font-strong text-6xl md:text-8xl lg:text-9xl text-red-600 drop-shadow-lg select-none mt-2"
                      style={{ textShadow: '2px 2px 0px rgba(255,255,255,0.8), 4px 4px 10px rgba(0,0,0,0.1)' }}
                  >
                    Valentine's Day
                  </h1>
                  
                  <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5">
                    <Heart size={400} fill="red" />
                  </div>
                </div>
                
                <p className="font-sub text-red-500 mt-6 text-2xl md:text-3xl animate-fade-in opacity-80 uppercase tracking-widest font-bold">
                    Dale, You Are My Everything
                </p>
            </div>
          </div>
        </>
      )}
      
    </div>
  );
};

export default App;